import React from 'react';
import {withSystem, Plugins, Component, Atom} from '@packages/systems/wfdl';
import {iconName, qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {px} from '@packages/systems/core/utils/WebflowCssSubset';
import {createIntegratedAtom} from '@packages/utilities/integrated-types';
import {
  EvaluatedNodeData,
  getChildren,
  getString,
  getInPojo,
} from '@packages/utilities/data-utils';
import {
  TListOfElement,
  TRecord,
  TEnum,
  EElement,
  EText,
  ERecord,
  EBoolean,
  ENumber,
  EEnum,
  EList,
  ELiteral,
} from '@packages/systems/wfdl-ast';

import {
  updatePasswordErrorStates,
  ERROR_STATE,
  UPDATE_PASSWORD_UI_ERROR_CODES,
} from '@packages/systems/users/constants';
import {Map} from 'immutable';

const UserUpdatePasswordFormWrapper = Atom.createRender(
  ({attributes, classes, generateAttributes, data, renderChild}) => {
    const formState = getString(getInPojo(data, ['temp', 'state'])) ?? 'normal';

    const context = Map({formState});

    return (
      <div {...generateAttributes(attributes, classes)}>
        {getChildren(data).map((child: EvaluatedNodeData) =>
          renderChild(child, context)
        )}
      </div>
    );
  }
);

const config = createIntegratedAtom({
  constraints: {
    descendants: [
      {
        is: 'UserUpdatePasswordForm',
        rule: 'ExactlyOne',
      },
      {
        is: 'UserFormErrorState',
        rule: 'ExactlyOne',
      },
      {
        is: 'UserFormSuccessState',
        rule: 'ExactlyOne',
      },
    ],
    page: [
      {
        type: 'UtilityPage',
        is: 'usys-update-password',
      },
    ],
  },
  displayName: 'Update Password Block',
  icon: {
    type: 'Component',
    value: qualify(Plugins.name('Form'), iconName('FormBlock')),
  },
  render: UserUpdatePasswordFormWrapper,
  showEmpty: true,
  fields: {
    temp: TRecord({
      state: withSystem(
        {label: 'State', defaultValue: 'normal', isFullWidth: true},
        TEnum({
          normal: 'Normal',
          success: 'Success',
          error: 'Error',
        })
      ),
    }),
    children: TListOfElement,
  },
  style: {
    base: {
      main: {
        noPseudo: {
          position: 'relative',
          marginBottom: px(0),
          paddingLeft: px(20),
          paddingRight: px(20),
        },
      },
    },
  },
});

export default config;

export const UserUpdatePasswordFormPreset = EElement({
  id: 'User Update Password Form Wrapper',
  type: qualify(
    Plugins.name('Users'),
    Component.name('UserUpdatePasswordFormWrapper')
  ),
  data: ERecord({
    temp: ERecord({
      state: EEnum('normal'),
    }),
    children: EList([
      EElement({
        id: 'User Update Password Success Message',
        type: qualify(
          Plugins.name('Users'),
          Component.name('UserFormSuccessState')
        ),
        data: ERecord({
          children: EList([
            EElement({
              id: 'Header',
              type: qualify(
                Plugins.name('Users'),
                Component.name('UserFormHeader')
              ),
              data: ERecord({
                children: EList([
                  EElement({
                    id: 'Image',
                    type: qualify(
                      Plugins.name('Basic'),
                      Component.name('Image')
                    ),
                    data: ERecord({
                      img: ELiteral({
                        name: qualify(Plugins.name('Asset'), typeName('Image')),
                        value: {},
                      }),
                      attr: ERecord({
                        src: EText(
                          'https://d3e54v103j8qbb.cloudfront.net/img/thumbs-up-72.cbcaec93bc.svg'
                        ),
                        loading: EEnum('lazy'),
                        width: EText('auto'),
                        height: EText('auto'),
                        alt: EText('__wf_reserved_decorative'),
                      }),
                      temp: ERecord({
                        img: ERecord({
                          src: EText(''),
                        }),
                      }),
                      srcsetDisabled: EBoolean(false),
                      sizes: EList([]),
                    }),
                  }),
                  EElement({
                    id: 'Body Heading',
                    type: qualify(
                      Plugins.name('Basic'),
                      Component.name('Heading')
                    ),
                    data: ERecord({
                      tag: EEnum('h2'),
                      children: EList([
                        EElement({
                          id: 'Body String',
                          type: qualify(
                            Plugins.name('Basic'),
                            Component.name('String')
                          ),
                          data: EText('Password updated'),
                        }),
                      ]),
                    }),
                  }),
                ]),
              }),
            }),
            EElement({
              id: 'Body Paragraph',
              type: qualify(Plugins.name('Basic'), Component.name('Paragraph')),
              data: ERecord({
                children: EList([
                  EElement({
                    id: 'Body String 2',
                    type: qualify(
                      Plugins.name('Basic'),
                      Component.name('String')
                    ),
                    data: EText(
                      'Your password was updated successfully. You were automatically logged in and can continue your session.'
                    ),
                  }),
                ]),
              }),
            }),
            EElement({
              id: 'Body Link',
              type: qualify(Plugins.name('Basic'), Component.name('Link')),
              data: ERecord({
                button: EBoolean(true),
                block: EText(''),
                eventIds: EList([]),
                link: ELiteral({
                  name: qualify(Plugins.name('Basic'), typeName('Link')),
                  value: {
                    mode: 'external',
                    url: '/',
                  },
                }),
                children: EList([
                  EElement({
                    id: 'Body String 3',
                    type: qualify(
                      Plugins.name('Basic'),
                      Component.name('String')
                    ),
                    data: EText('Go to Homepage'),
                  }),
                ]),
              }),
            }),
          ]),
        }),
      }),
      EElement({
        id: 'User Update Password Form',
        type: qualify(
          Plugins.name('Users'),
          Component.name('UserUpdatePasswordForm')
        ),
        data: ERecord({
          children: EList([
            EElement({
              id: 'User Form Header',
              type: qualify(
                Plugins.name('Users'),
                Component.name('UserFormHeader')
              ),
              data: ERecord({
                children: EList([
                  EElement({
                    id: 'Body Heading 2',
                    type: qualify(
                      Plugins.name('Basic'),
                      Component.name('Heading')
                    ),
                    data: ERecord({
                      tag: EEnum('h2'),
                      children: EList([
                        EElement({
                          id: 'Body String 4',
                          type: qualify(
                            Plugins.name('Basic'),
                            Component.name('String')
                          ),
                          data: EText('Update Password'),
                        }),
                      ]),
                    }),
                  }),
                ]),
              }),
            }),
            EElement({
              id: 'Body Paragraph 2',
              type: qualify(Plugins.name('Basic'), Component.name('Paragraph')),
              data: ERecord({
                attr: ERecord({
                  id: EText('wf-update-password-paragraph'),
                }),
                children: EList([
                  EElement({
                    id: 'Body String 5',
                    type: qualify(
                      Plugins.name('Basic'),
                      Component.name('String')
                    ),
                    data: EText(
                      'Please enter your new password in the field below. Make sure you are not using your current password.'
                    ),
                  }),
                ]),
              }),
            }),
            EElement({
              id: 'User Form Block Label',
              type: qualify(
                Plugins.name('Users'),
                Component.name('UserFormBlockLabel')
              ),
              data: ERecord({
                attr: ERecord({
                  for: EText('wf-update-password'),
                }),
                users: ERecord({
                  field: EText(''),
                }),
                children: EList([
                  EElement({
                    id: 'Body String 6',
                    type: qualify(
                      Plugins.name('Basic'),
                      Component.name('String')
                    ),
                    data: EText('New Password'),
                  }),
                ]),
              }),
            }),
            EElement({
              id: 'User Form Password Input',
              type: qualify(
                Plugins.name('Users'),
                Component.name('UserFormPasswordInput')
              ),
              data: ERecord({
                attr: ERecord({
                  autofocus: EBoolean(false),
                  maxlength: ENumber(256),
                  placeholder: EText(''),
                  disabled: EBoolean(false),
                  name: EText('Password'),
                  id: EText('wf-update-password'),
                }),
                xattr: EList([
                  ERecord({
                    name: EText('aria-describedby'),
                    value: EText('wf-update-password-paragraph'),
                  }),
                ]),
              }),
            }),
            EElement({
              id: 'User Form Button',
              type: qualify(
                Plugins.name('Users'),
                Component.name('UserFormButton')
              ),
              data: ERecord({
                attr: ERecord({
                  type: EText('submit'),
                  value: EText('Update Password'),
                  'data-wait': EText('Please wait...'),
                }),
              }),
            }),
          ]),
        }),
      }),
      EElement({
        id: 'User Update Password Error State',
        type: qualify(
          Plugins.name('Users'),
          Component.name('UserFormErrorState')
        ),
        data: ERecord({
          users: ERecord({
            type: EText(ERROR_STATE.UPDATE_PASSWORD),
          }),
          children: EList([
            EElement({
              id: 'User Update Password Error Msg',
              type: qualify(
                Plugins.name('Users'),
                Component.name('UserErrorMsg')
              ),
              data: ERecord({
                temp: ERecord({
                  index: ENumber(0),
                }),
                users: ERecord({
                  type: EText(ERROR_STATE.UPDATE_PASSWORD),
                }),
                errors: EList([
                  ERecord({
                    id: EText(
                      // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
                      updatePasswordErrorStates[
                        UPDATE_PASSWORD_UI_ERROR_CODES.GENERAL_ERROR
                      ].id
                    ),
                    name: EText(
                      // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
                      updatePasswordErrorStates[
                        UPDATE_PASSWORD_UI_ERROR_CODES.GENERAL_ERROR
                      ].name
                    ),
                    copy: EText(
                      // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
                      updatePasswordErrorStates[
                        UPDATE_PASSWORD_UI_ERROR_CODES.GENERAL_ERROR
                      ].copy
                    ),
                  }),
                  ERecord({
                    id: EText(
                      // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
                      updatePasswordErrorStates[
                        UPDATE_PASSWORD_UI_ERROR_CODES.WEAK_PASSWORD
                      ].id
                    ),
                    name: EText(
                      // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
                      updatePasswordErrorStates[
                        UPDATE_PASSWORD_UI_ERROR_CODES.WEAK_PASSWORD
                      ].name
                    ),
                    copy: EText(
                      // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
                      updatePasswordErrorStates[
                        UPDATE_PASSWORD_UI_ERROR_CODES.WEAK_PASSWORD
                      ].copy
                    ),
                  }),
                ]),
              }),
            }),
          ]),
        }),
      }),
    ]),
  }),
});
